import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";

import { useEffect, useState, useMemo } from "react";
import cn from "classnames";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide, useSwiperSlide } from "swiper/react";
import Box from "@mui/material/Box";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { SwipeTracking } from "@/src/component/partial/SwipeTracking";
import { StyleHooks } from "@/src/hook";
import CMSLogic from "@/src/logic/CMSLogic";
import { StrapiComponentType, ComponentType } from "@/src/type";
import { LoadFromCDN } from "@/src/util";

import { SectionModule, TextModule, TextBreakpointModule, ButtonLinkModule } from "../component";
import CarouselButtons from "./CarouselButtons";

import styles from "./Carousel.module.scss";
import { FrontendTrackingService } from "@/src/service";

function autoScrollOptions(autoScrollOptions?: StrapiComponentType.Section.CarouselAutoScrollOptions) {
  switch (autoScrollOptions ?? "off") {
    case "lgAndBelow":
      return "lg";
    case "mdAndBelow":
      return "md";
    case "smAndBelow":
      return "sm";
    default:
      return "md";
  }
}

interface CarouselProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.Carousel> {}

function Carousel({
  section,
  slug,
  snapToGrid,
  lazy,
  classes,
  searchParams,
  quality,
  userAgent,
  layout,
  mobileHint,
  sectionTypePath,
}: CarouselProps) {
  const swiperSlide = useSwiperSlide();
  const [swiper, setSwiper] = useState<any>(null);
  const carousel: StrapiComponentType.Section.Carousel = section.type?.[0];
  const id = `Carousel-${carousel.id}`;
  const pagination = `carousel-page-${carousel.id}`;
  const isMobile = StyleHooks.useBreakPointDown("md");
  const autoScrollBreakpoint = StyleHooks.useBreakPointDown(autoScrollOptions(carousel.autoScrollOptions));
  const cards = (carousel?.cards ?? []).length <= 3 ? [...carousel?.cards, ...carousel?.cards] : carousel?.cards; //&& !mobileHint

  /**
   * Swiper uses class name to add required onClick for prev/next functionality
   * Set the max number of results (todo: move this to CMS global web config)
   */
  const [prevButton] = useState<string>(`carousel-prev_${id}`);
  const [nextButton] = useState<string>(`carousel-next_${id}`);

  /**
   * this creates the pagination items for the bottom of the screen on mobile
   * Swiper does not accept the react/JSX way of rendering components and must return a string
   */
  const renderPaginationItem = (index: number, className: string) => {
    const itemStyles = cn(
      styles.paginationItem,
      carousel?.paginationIconColor === "primary" ? styles.primary : styles.secondary,
      carousel?.paginationIcon === "dots" ? styles.dots : undefined
    );
    return `<span class="${className} ${itemStyles}"></span>`;
  };

  const textColor = () => {
    switch (carousel.cardTextColor ?? "secondary") {
      case "primary":
        return "primaryMain";
      case "secondary":
      default:
        return "secondaryMain";
    }
  };

  /**
   * this determines based on cms settings when the carousel should auto scroll
   * https://swiperjs.com/swiper-api#autoplay
   */
  const autoScroll = useMemo(() => {
    const interval = carousel.autoScrollIntervalMs ?? 3000;
    switch (carousel.autoScrollOptions ?? "off") {
      case "lgAndBelow":
      case "mdAndBelow":
      case "smAndBelow":
        return autoScrollBreakpoint ? { delay: interval } : false;
      case "on":
        return { delay: interval };
      default:
        return false;
    }
  }, [carousel, autoScrollBreakpoint]);

  /**
   * card title font
   */
  const cardTitleFont = () => {
    switch (carousel.cardTitleFont ?? "default") {
      case "poppins18px":
        return styles.poppins18px;
    }
  };

  /**
   * card subtitle font
   */
  const cardSubtitleFont = () => {
    switch (carousel.cardSubtitleFont ?? "default") {
      case "poppins15px":
        return styles.poppins15px;
      case "poppins14px":
        return styles.poppins14px;
    }
  };

  /**
   * pause the auto swiper effect when carousel is not in focus
   */
  useEffect(() => {
    //don't trigger until swipper is fully mounted
    if (!swiper || typeof swiper?.destroy === "boolean" || !swiper?.autoplay) {
      return;
    }

    if (!autoScroll) {
      if (swiper?.running) {
        swiper?.autoplay.pause();
      }
      return;
    }

    if (!swiperSlide.isActive) {
      swiper?.autoplay.pause();
    } else if (!swiper?.running) {
      swiper?.autoplay.start();
      swiper?.autoplay.resume();
    }
  }, [swiperSlide.isActive, autoScroll, swiper]);

  return (
    <SectionModule
      section={section}
      slug={slug}
      layout={layout}
      classes={{
        section: cn(styles.section, classes?.section),
      }}
      stylePrefix="Carousel"
      styleContainerId={carousel.id}
      snapToGrid={snapToGrid}
      searchParams={searchParams}
      quality={quality}
      lazy={lazy}
    >
      <SwipeTracking
        selector={`#swiper_${id}`}
        onSwipe={() => {
          FrontendTrackingService.postTrackingLog({
            action: "swiped",
            actionId: `carousel:${carousel.id}`,
            actionIdType: "componentName:id",
            //actionId2: `carousel.cards.${swiper?.activeIndex}`,
            //actionId2Type: "componentName:property.id",
            actionMessage: `Carousel section swiped with ${section.name} and index slide ${(swiper?.activeIndex ?? 0) + 1} with auto scroll ${swiper?.autoplay?.running ? "on" : "off"}`,
            triggeredFrom: "Carousel.tsx:(SwipeTracking.tsx(onSwipe))",
          });
        }}
      />
      {(carousel?.cards ?? []).length > 3 && (
        <CarouselButtons
          prevClassName={prevButton}
          nextClassName={nextButton}
          classes={{
            prevClassName: styles.buttonPrev,
            nextClassName: styles.buttonNext,
          }}
          color={carousel?.cardTextColor}
          tracking={{
            action: "buttonClicked",
            actionId: `carousel:${carousel.id}`,
            actionIdType: "componentName:componentId",
            actionMessage: `Carousel section swiped with ${section.name} and index slide ${(swiper?.activeIndex ?? 0) + 1} with auto scroll ${swiper?.autoplay?.running ? "on" : "off"}`,
            triggeredFrom: "Carousel.tsx:(CarouselButtons.tsx(tracking))",
          }}
        />
      )}
      <div className={styles.titleElevation}>
        <TextBreakpointModule
          id={`carousel_title_${carousel.id}`}
          text={section.title}
          className={styles.title}
          sectionTypePath={sectionTypePath}
        />
      </div>
      <Swiper
        id={`swiper_${id}`}
        className={cn(styles.swiper, snapToGrid ? styles.snapToGrid : undefined)}
        modules={[Navigation, Pagination, Autoplay]}
        centerInsufficientSlides={isMobile}
        centeredSlides={isMobile}
        spaceBetween={isMobile ? 0 : -1}
        slidesPerView={isMobile ? 1 : 3}
        pagination={{
          el: `.${pagination}`,
          clickable: true,
          renderBullet: renderPaginationItem,
        }}
        //this causing re-sizing issues
        //loop={isMobile || (carousel?.cards ?? []).length > 3}
        loop={true}
        direction="horizontal"
        onSwiper={setSwiper}
        navigation={{
          prevEl: `#${prevButton}`,
          nextEl: `#${nextButton}`,
        }}
        autoplay={autoScroll}
        userAgent={userAgent}
      >
        {(cards ?? []).map((card, i) => {
          // when there is a mobile hint, only load the first card in swiper to reduce LCP
          const cardIsLazy = i === 0 ? "eager" : mobileHint ? "lazy" : lazy;
          return (
            <SwiperSlide
              key={`${card.id}_${i}`}
              className={cn(styles.card, card.cta ? styles.cardHover : undefined, snapToGrid ? styles.snapToGrid : undefined)}
            >
              <ImgBgResponsive
                className={styles.background}
                imageUrl={LoadFromCDN.nextGenFormat(
                  LoadFromCDN.imageQuality(CMSLogic.imageLinkSelection(card?.backgroundImage.data?.attributes), quality)
                )}
                lazy={cardIsLazy}
                prefetch={cardIsLazy === "eager"}
                triggers={["swiperSlideChange", "click", "touchstart", "scroll"]}
              >
                {card?.overlayImage?.data?.attributes && (
                  <div className={styles.overlayImageContainer}>
                    <ImgBgResponsive
                      className={styles.overlayImage}
                      imageUrl={LoadFromCDN.nextGenFormat(CMSLogic.imageLinkSelection(card?.overlayImage?.data?.attributes))}
                      lazy={cardIsLazy}
                      prefetch={cardIsLazy === "eager"}
                      triggers={["swiperSlideChange", "click", "touchstart", "scroll"]}
                    />
                  </div>
                )}
                <div className={cn(styles.inner, carousel?.cardTextBgShadowOff ? styles.bgShadowOff : undefined)}>
                  <div className={styles.innerText}>
                    <TextModule
                      text={{
                        tag: "body1",
                        text: card.title,
                        color: textColor(),
                      }}
                      className={cn(styles.cardTitle, cardTitleFont())}
                    />
                    <TextModule
                      text={{
                        tag: "body1",
                        text: card.subtitle,
                        color: textColor(),
                      }}
                      className={cn(styles.cardSubtitle, cardSubtitleFont())}
                    />
                  </div>
                  {card.cta && (
                    <div className={styles.cta}>
                      <ButtonLinkModule buttonLink={card.cta} />
                    </div>
                  )}
                </div>
                <Box
                  className={cn(styles.textBackgroundShadow, carousel?.cardTextBgShadowOff ? styles.bgShadowOff : undefined)}
                  sx={{
                    height: CMSLogic.createCssValue(carousel.cardTextBgShadowHeight, carousel.cardTextBgShadowHeightUnit),
                  }}
                />
              </ImgBgResponsive>
            </SwiperSlide>
          );
        })}
        <div className={cn(pagination, styles.pagination, carousel.paginationIcon === "dots" ? styles.dots : undefined)}></div>
      </Swiper>
    </SectionModule>
  );
}

export default Carousel;
